import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, Lesson, LessonDetail, Question, CreateQuestionRequest } from '../api';
import { MathText } from '../components/MathText';
import './Lessons.css';

export default function Lessons() {
  const { user } = useAuth();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<LessonDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create lesson form
  const [showCreateLesson, setShowCreateLesson] = useState(false);
  const [newLesson, setNewLesson] = useState({
    lessonNumber: 1,
    title: '',
    description: '',
    gradeLevel: 7,
  });

  // Add question form
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    questionText: '',
    correctAnswer: '',
    explanation: '',
    difficultyLevel: 0.5,
  });

  // Bulk import
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');

  // Edit mode
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);

  useEffect(() => {
    loadLessons();
  }, []);

  const loadLessons = async () => {
    try {
      setLoading(true);
      const data = await api.getLessons();
      setLessons(data);
    } catch (err) {
      setError('Failed to load lessons');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLesson = async (id: number) => {
    try {
      const data = await api.getLesson(id);
      setSelectedLesson(data);
    } catch (err) {
      setError('Failed to load lesson details');
      console.error(err);
    }
  };

  const handleCreateLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.createLesson(newLesson);
      setShowCreateLesson(false);
      setNewLesson({ lessonNumber: 1, title: '', description: '', gradeLevel: 7 });
      loadLessons();
    } catch (err) {
      setError('Failed to create lesson');
      console.error(err);
    }
  };

  const handleDeleteLesson = async (id: number) => {
    if (!confirm('Delete this lesson? This cannot be undone.')) return;
    try {
      await api.deleteLesson(id);
      if (selectedLesson?.id === id) {
        setSelectedLesson(null);
      }
      loadLessons();
    } catch (err: any) {
      setError(err.message || 'Failed to delete lesson');
    }
  };

  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLesson) return;
    try {
      await api.addQuestion(selectedLesson.id, newQuestion);
      setShowAddQuestion(false);
      setNewQuestion({ questionText: '', correctAnswer: '', explanation: '', difficultyLevel: 0.5 });
      loadLesson(selectedLesson.id);
    } catch (err) {
      setError('Failed to add question');
      console.error(err);
    }
  };

  const handleUpdateQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingQuestion) return;
    try {
      await api.updateQuestion(editingQuestion.id, {
        questionText: editingQuestion.questionText,
        correctAnswer: editingQuestion.correctAnswer,
        explanation: editingQuestion.explanation,
        difficultyLevel: editingQuestion.difficultyLevel,
      });
      setEditingQuestion(null);
      if (selectedLesson) loadLesson(selectedLesson.id);
    } catch (err) {
      setError('Failed to update question');
      console.error(err);
    }
  };

  const handleDeleteQuestion = async (id: number) => {
    if (!confirm('Delete this question?')) return;
    try {
      await api.deleteQuestion(id);
      if (selectedLesson) loadLesson(selectedLesson.id);
    } catch (err) {
      setError('Failed to delete question');
      console.error(err);
    }
  };

  const handleBulkImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLesson || !bulkText.trim()) return;

    // Parse bulk text: each line is "Question | Answer" or "Question | Answer | Explanation"
    const lines = bulkText.trim().split('\n').filter(l => l.trim());
    const questions: CreateQuestionRequest[] = [];

    for (const line of lines) {
      const parts = line.split('|').map(p => p.trim());
      if (parts.length >= 2) {
        questions.push({
          questionText: parts[0],
          correctAnswer: parts[1],
          explanation: parts[2] || undefined,
          difficultyLevel: 0.5,
        });
      }
    }

    if (questions.length === 0) {
      setError('No valid questions found. Format: Question | Answer | Explanation (optional)');
      return;
    }

    try {
      const result = await api.bulkImportQuestions(selectedLesson.id, questions);
      setShowBulkImport(false);
      setBulkText('');
      loadLesson(selectedLesson.id);
      alert(`Imported ${result.insertedCount} questions`);
    } catch (err) {
      setError('Failed to import questions');
      console.error(err);
    }
  };

  if (!user?.isTeacher) {
    return (
      <div className="lessons-page">
        <p>Access denied. Teachers only.</p>
        <Link to="/dashboard">Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="lessons-page">
      <header className="header">
        <h1>Lesson Management</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreateLesson(true)}>
            + New Lesson
          </button>
          <Link to="/teacher" className="btn btn-secondary">Back to Dashboard</Link>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="lessons-layout">
        {/* Lessons List */}
        <div className="lessons-list">
          <h2>Lessons</h2>
          {loading ? (
            <p>Loading...</p>
          ) : lessons.length === 0 ? (
            <p>No lessons yet. Create your first lesson!</p>
          ) : (
            <ul>
              {lessons.map(lesson => (
                <li
                  key={lesson.id}
                  className={selectedLesson?.id === lesson.id ? 'selected' : ''}
                  onClick={() => loadLesson(lesson.id)}
                >
                  <span className="lesson-number">L{lesson.lessonNumber}</span>
                  <span className="lesson-title">{lesson.title}</span>
                  <span className="lesson-meta">
                    Grade {lesson.gradeLevel} | {lesson.questionCount} Q
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Lesson Detail */}
        <div className="lesson-detail">
          {selectedLesson ? (
            <>
              <div className="lesson-header">
                <h2>
                  Lesson {selectedLesson.lessonNumber}: {selectedLesson.title}
                </h2>
                <div className="lesson-actions">
                  <button className="btn btn-primary" onClick={() => setShowAddQuestion(true)}>
                    + Add Question
                  </button>
                  <button className="btn btn-secondary" onClick={() => setShowBulkImport(true)}>
                    Bulk Import
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDeleteLesson(selectedLesson.id)}
                  >
                    Delete Lesson
                  </button>
                </div>
              </div>

              {selectedLesson.description && (
                <p className="lesson-description">{selectedLesson.description}</p>
              )}

              <h3>Questions ({selectedLesson.questions.length})</h3>

              {selectedLesson.questions.length === 0 ? (
                <p>No questions yet. Add some questions to this lesson.</p>
              ) : (
                <div className="questions-list">
                  {selectedLesson.questions.map((q, i) => (
                    <div key={q.id} className="question-card">
                      {editingQuestion?.id === q.id ? (
                        <form onSubmit={handleUpdateQuestion} className="edit-form">
                          <div className="form-group">
                            <label>Question:</label>
                            <textarea
                              value={editingQuestion.questionText}
                              onChange={e => setEditingQuestion({
                                ...editingQuestion,
                                questionText: e.target.value
                              })}
                              rows={2}
                            />
                          </div>
                          <div className="form-group">
                            <label>Answer:</label>
                            <input
                              type="text"
                              value={editingQuestion.correctAnswer}
                              onChange={e => setEditingQuestion({
                                ...editingQuestion,
                                correctAnswer: e.target.value
                              })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Explanation:</label>
                            <textarea
                              value={editingQuestion.explanation || ''}
                              onChange={e => setEditingQuestion({
                                ...editingQuestion,
                                explanation: e.target.value
                              })}
                              rows={2}
                            />
                          </div>
                          <div className="form-actions">
                            <button type="submit" className="btn btn-primary">Save</button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setEditingQuestion(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <>
                          <div className="question-number">#{i + 1}</div>
                          <div className="question-content">
                            <div className="question-text">
                              <MathText>{q.questionText}</MathText>
                            </div>
                            <div className="question-answer">
                              <strong>Answer:</strong> <MathText>{q.correctAnswer}</MathText>
                            </div>
                            {q.explanation && (
                              <div className="question-explanation">
                                <strong>Explanation:</strong> {q.explanation}
                              </div>
                            )}
                            <div className="question-stats">
                              Difficulty: {Math.round(q.difficultyLevel * 100)}% |
                              Used: {q.timesUsed || 0} |
                              Correct: {q.timesCorrect || 0}
                            </div>
                          </div>
                          <div className="question-actions">
                            <button
                              className="btn btn-small"
                              onClick={() => setEditingQuestion(q)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => handleDeleteQuestion(q.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <p>Select a lesson from the list to view and manage questions.</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Lesson Modal */}
      {showCreateLesson && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Create New Lesson</h2>
            <form onSubmit={handleCreateLesson}>
              <div className="form-group">
                <label>Lesson Number:</label>
                <input
                  type="number"
                  value={newLesson.lessonNumber}
                  onChange={e => setNewLesson({ ...newLesson, lessonNumber: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Title:</label>
                <input
                  type="text"
                  value={newLesson.title}
                  onChange={e => setNewLesson({ ...newLesson, title: e.target.value })}
                  placeholder="e.g., Adding Fractions"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={newLesson.description}
                  onChange={e => setNewLesson({ ...newLesson, description: e.target.value })}
                  placeholder="Brief description of this lesson..."
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Grade Level:</label>
                <select
                  value={newLesson.gradeLevel}
                  onChange={e => setNewLesson({ ...newLesson, gradeLevel: parseInt(e.target.value) })}
                >
                  {[5, 6, 7, 8, 9, 10, 11, 12].map(g => (
                    <option key={g} value={g}>Grade {g}</option>
                  ))}
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Create Lesson</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateLesson(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Question Modal */}
      {showAddQuestion && selectedLesson && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Question to Lesson {selectedLesson.lessonNumber}</h2>
            <form onSubmit={handleAddQuestion}>
              <div className="form-group">
                <label>Question Text:</label>
                <textarea
                  value={newQuestion.questionText}
                  onChange={e => setNewQuestion({ ...newQuestion, questionText: e.target.value })}
                  placeholder="Enter the question... Use $ for math: $\frac{1}{2}$"
                  rows={3}
                  required
                />
              </div>
              <div className="form-group">
                <label>Correct Answer:</label>
                <input
                  type="text"
                  value={newQuestion.correctAnswer}
                  onChange={e => setNewQuestion({ ...newQuestion, correctAnswer: e.target.value })}
                  placeholder="e.g., 42 or $\frac{3}{4}$"
                  required
                />
              </div>
              <div className="form-group">
                <label>Explanation (optional):</label>
                <textarea
                  value={newQuestion.explanation}
                  onChange={e => setNewQuestion({ ...newQuestion, explanation: e.target.value })}
                  placeholder="How to solve this problem..."
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>Difficulty (0-100%):</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={newQuestion.difficultyLevel * 100}
                  onChange={e => setNewQuestion({
                    ...newQuestion,
                    difficultyLevel: parseInt(e.target.value) / 100
                  })}
                />
                <span>{Math.round(newQuestion.difficultyLevel * 100)}%</span>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Add Question</button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddQuestion(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && selectedLesson && (
        <div className="modal-overlay">
          <div className="modal modal-wide">
            <h2>Bulk Import Questions</h2>
            <p className="help-text">
              Enter one question per line. Format: <code>Question | Answer | Explanation (optional)</code>
            </p>
            <form onSubmit={handleBulkImport}>
              <div className="form-group">
                <textarea
                  value={bulkText}
                  onChange={e => setBulkText(e.target.value)}
                  placeholder={`What is 2 + 2? | 4 | Add the numbers together\nWhat is $\\frac{1}{2} + \\frac{1}{2}$? | 1\nSimplify $3x + 2x$ | 5x | Combine like terms`}
                  rows={10}
                  className="bulk-textarea"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  Import Questions
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowBulkImport(false); setBulkText(''); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
